import { Injectable } from '@angular/core';
import { HttpRestService } from '@core/services/http-rest.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { ApplicationFileUploadResponse, FileValidationResult } from '@core/typings/file.typing';

@Injectable({ providedIn: 'root' })
export class ApplicationFileResources {
  constructor (
    private http: HttpRestService,
    private portal: PortalDeterminationService
  ) { }

  uploadFile (
    applicationId: number,
    applicationFormId: number,
    refFieldId: number,
    file: File,
    fileName: string
  ): Promise<number> {
    const forApplicant = this.portal.isApply;
      let endpoint = forApplicant ?
        `/api/portal/applications/${applicationId}/ApplicationForms/${applicationFormId}/Files` :
        `/api/manager/applications/${applicationId}/ApplicationForms/${applicationFormId}/Files`;
      if (!!refFieldId) {
        endpoint = `${endpoint}?referenceFieldId=${refFieldId}`;
      }

    return this.http.postFile(endpoint, file, undefined, fileName);
  }

  private getFileApplicant (
    applicationId: number,
    applicationFormId: number,
    applicationFormFileUploadId: number
  ) {
    return `/api/portal/applications/${applicationId}/ApplicationForms/${applicationFormId}/Files/${applicationFormFileUploadId}`;
  }

  private getFileManager (
    applicationId: number,
    applicationFormId: number,
    applicationFormFileUploadId: number
  ) {
    return `/api/manager/applications/${applicationId}/ApplicationForms/${applicationFormId}/Files/${applicationFormFileUploadId}`;
  }

  async getFile (
    applicationId: number,
    applicationFormId: number,
    applicationFormFileUploadId: number
  ) {
    return this.http.get<ApplicationFileUploadResponse>(
      this.getFileUrl(applicationId, applicationFormId, applicationFormFileUploadId)
    );
  }

  /**
   * Validates if a file upload is still active and accessible on the backend.
   * Returns a validation result indicating whether the file is valid or has become stale/deactivated.
   *
   * @param applicationId The application ID
   * @param applicationFormId The application form ID
   * @param fileUploadId The file upload ID to validate
   * @returns FileValidationResult indicating if the file is still valid
   */
  async validateFileUpload (
    applicationId: number,
    applicationFormId: number,
    fileUploadId: number
  ): Promise<FileValidationResult> {
    await this.http.get<ApplicationFileUploadResponse>(
      this.getFileUrl(applicationId, applicationFormId, fileUploadId)
    );

    return {
      fileUploadId,
      isValid: true
    };
  }

  private getFileUrl (
    applicationId: number,
    applicationFormId: number,
    applicationFormFileUploadId: number
  ): string {
    const func = this.portal.isApply ?
      'getFileApplicant' :
      'getFileManager';

    return this[func](
      applicationId,
      applicationFormId,
      applicationFormFileUploadId
    );
  }
}
